<script>
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import ReportsFiltersDateRange from './Filters/DateRange.vue';
import ReportsFiltersDateGroupBy from './Filters/DateGroupBy.vue';
import ReportsFiltersAgents from './Filters/Agents.vue';
import ReportsFiltersLabels from './Filters/Labels.vue';
import ReportsFiltersInboxes from './Filters/Inboxes.vue';
import ReportsFiltersTeams from './Filters/Teams.vue';
import ReportsFiltersRatings from './Filters/Ratings.vue';
import subDays from 'date-fns/subDays';
import set from 'date-fns/set';
import getUnixTime from 'date-fns/getUnixTime';
import { DATE_RANGE_OPTIONS } from '../constants';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

export default {
  components: {
    WootDateRangePicker,
    ReportsFiltersDateRange,
    ReportsFiltersDateGroupBy,
    ReportsFiltersAgents,
    ReportsFiltersLabels,
    ReportsFiltersInboxes,
    ReportsFiltersTeams,
    ReportsFiltersRatings,
    ToggleSwitch,
  },
  props: {
    showGroupByFilter: {
      type: Boolean,
      default: false,
    },
    showAgentsFilter: {
      type: Boolean,
      default: false,
    },
    showLabelsFilter: {
      type: Boolean,
      default: false,
    },
    showInboxFilter: {
      type: Boolean,
      default: false,
    },
    showRatingFilter: {
      type: Boolean,
      default: false,
    },
    showTeamFilter: {
      type: Boolean,
      default: false,
    },
    showBusinessHoursSwitch: {
      type: Boolean,
      default: true,
    },
    showTimeFilter: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['filterChange'],
  data() {
    return {
      // default value, need not be translated
      selectedDateRange: DATE_RANGE_OPTIONS.LAST_7_DAYS,
      selectedGroupByFilter: null,
      selectedLabel: null,
      selectedInbox: null,
      selectedTeam: null,
      selectedRating: null,
      selectedAgents: [],
      customDateRange: [new Date(), new Date()],
      businessHoursSelected: false,
      startTime: '00:00',
      endTime: '23:59',
    };
  },
  computed: {
    isDateRangeSelected() {
      return (
        this.selectedDateRange.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
      );
    },
    isGroupByPossible() {
      return (this.validGroupOptions || []).length > 1;
    },
    to() {
      if (this.isDateRangeSelected) {
        const baseDate = this.customDateRange[1];
        return this.resolveUnixEnd(baseDate);
      }
      const endOffset =
        this.selectedDateRange.endOffset ?? this.selectedDateRange.offset ?? 0;
      const toDate = subDays(new Date(), endOffset);
      return this.resolveUnixEnd(toDate);
    },
    from() {
      if (this.isDateRangeSelected) {
        const baseDate = this.customDateRange[0];
        return this.resolveUnixStart(baseDate);
      }

      const startOffset =
        this.selectedDateRange.startOffset ??
        this.selectedDateRange.offset ??
        0;
      const fromDate = subDays(new Date(), startOffset);
      return this.resolveUnixStart(fromDate);
    },
    validGroupOptions() {
      return this.selectedDateRange.groupByOptions || [];
    },
    validGroupBy() {
      if (!this.validGroupOptions.length) {
        return null;
      }

      if (!this.selectedGroupByFilter) {
        return this.validGroupOptions[0];
      }

      const validIds = this.validGroupOptions.map(opt => opt.id);
      if (validIds.includes(this.selectedGroupByFilter.id)) {
        return this.selectedGroupByFilter;
      }
      return this.validGroupOptions[0];
    },
  },
  watch: {
    startTime() {
      if (this.showTimeFilter) {
        this.emitChange();
      }
    },
    endTime() {
      if (this.showTimeFilter) {
        this.emitChange();
      }
    },
  },
  mounted() {
    this.selectedGroupByFilter = this.validGroupBy;
    this.emitChange();
  },
  methods: {
    resolveUnixStart(date) {
      if (!this.showTimeFilter) {
        return getUnixStartOfDay(date);
      }

      return this.combineDateAndTime(date, this.startTime, {
        endOfDayFallback: getUnixStartOfDay(date),
      });
    },
    resolveUnixEnd(date) {
      if (!this.showTimeFilter) {
        return getUnixEndOfDay(date);
      }

      return this.combineDateAndTime(date, this.endTime, {
        isEnd: true,
        endOfDayFallback: getUnixEndOfDay(date),
      });
    },
    combineDateAndTime(date, timeValue, options = {}) {
      const { isEnd = false, endOfDayFallback } = options;
      if (!timeValue) {
        return endOfDayFallback;
      }

      const [hours, minutes] = timeValue.split(':').map(value => Number(value));
      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return endOfDayFallback;
      }

      const targetDate = set(new Date(date), {
        hours,
        minutes,
        seconds: isEnd ? 59 : 0,
        milliseconds: isEnd ? 999 : 0,
      });

      return getUnixTime(targetDate);
    },
    emitChange() {
      const {
        from,
        to,
        selectedGroupByFilter: groupBy,
        businessHoursSelected: businessHours,
        selectedAgents,
        selectedLabel,
        selectedInbox,
        selectedTeam,
        selectedRating,
      } = this;
      this.$emit('filterChange', {
        from,
        to,
        groupBy,
        businessHours,
        selectedAgents,
        selectedLabel,
        selectedInbox,
        selectedTeam,
        selectedRating,
      });
    },
    onDateRangeChange(selectedRange) {
      this.selectedDateRange = selectedRange;
      this.selectedGroupByFilter = this.validGroupBy;
      this.emitChange();
    },
    onCustomDateRangeChange(value) {
      this.customDateRange = value;
      this.selectedGroupByFilter = this.validGroupBy;
      this.emitChange();
    },
    onGroupingChange(payload) {
      this.selectedGroupByFilter = payload;
      this.emitChange();
    },
    handleAgentsFilterSelection(selectedAgents) {
      this.selectedAgents = selectedAgents;
      this.emitChange();
    },
    handleLabelsFilterSelection(selectedLabel) {
      this.selectedLabel = selectedLabel;
      this.emitChange();
    },
    handleInboxFilterSelection(selectedInbox) {
      this.selectedInbox = selectedInbox;
      this.emitChange();
    },
    handleTeamFilterSelection(selectedTeam) {
      this.selectedTeam = selectedTeam;
      this.emitChange();
    },
    handleRatingFilterSelection(selectedRating) {
      this.selectedRating = selectedRating;
      this.emitChange();
    },
  },
};
</script>

<template>
  <div class="flex flex-col justify-between gap-3 md:flex-row md:items-end">
    <div
      class="w-full grid gap-y-2 gap-x-1.5 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]"
    >
      <div class="md:self-center">
        <ReportsFiltersDateRange @on-range-change="onDateRangeChange" />
      </div>
      <WootDateRangePicker
        v-if="isDateRangeSelected"
        show-range
        class="no-margin auto-width md:self-end"
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onCustomDateRangeChange"
      />
      <ReportsFiltersDateGroupBy
        v-if="showGroupByFilter && isGroupByPossible"
        :valid-group-options="validGroupOptions"
        :selected-option="selectedGroupByFilter"
        @on-grouping-change="onGroupingChange"
      />
      <ReportsFiltersAgents
        v-if="showAgentsFilter"
        @agents-filter-selection="handleAgentsFilterSelection"
      />
      <ReportsFiltersLabels
        v-if="showLabelsFilter"
        @labels-filter-selection="handleLabelsFilterSelection"
      />
      <ReportsFiltersTeams
        v-if="showTeamFilter"
        @team-filter-selection="handleTeamFilterSelection"
      />
      <ReportsFiltersInboxes
        v-if="showInboxFilter"
        @inbox-filter-selection="handleInboxFilterSelection"
      />
      <ReportsFiltersRatings
        v-if="showRatingFilter"
        @rating-filter-selection="handleRatingFilterSelection"
      />
      <div
        v-if="showTimeFilter"
        class="order-3 md:order-3 grid w-full gap-3 sm:grid-cols-2 md:self-end"
      >
        <div class="flex flex-col gap-2">
          <label
            class="text-xs font-medium text-n-slate-11"
            for="vip-start-time"
          >
            {{ $t('REPORT.START_TIME_LABEL') }}
          </label>
          <input
            id="vip-start-time"
            v-model="startTime"
            type="time"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-xs font-medium text-n-slate-11" for="vip-end-time">
            {{ $t('REPORT.END_TIME_LABEL') }}
          </label>
          <input
            id="vip-end-time"
            v-model="endTime"
            type="time"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
    </div>
    <div v-if="showBusinessHoursSwitch" class="flex items-center">
      <span class="mx-2 text-sm whitespace-nowrap">
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </span>
      <span>
        <ToggleSwitch v-model="businessHoursSelected" @change="emitChange" />
      </span>
    </div>
  </div>
</template>
